import { FC, useMemo, useEffect } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { lightScrollbarStyle } from "../../styles/lightScrollbarStyle";
import { useFeatureFlag } from "../../hooks/useLaunchDarkly";
import { AppLayout } from "../../components/layout/AppLayout";

interface BreadcrumbItemProps {
  label: string;
  onClick?: () => void;
  isActive?: boolean;
}

interface TabConfig {
  path: string;
  label: string;
  breadcrumbLabel?: string; // Optional custom breadcrumb label
  featureFlagKey?: string;
}

const BreadcrumbItem: FC<BreadcrumbItemProps> = ({
  label,
  onClick,
  isActive = false,
}) => {
  return (
    <Typography
      fontSize="11px"
      fontWeight={300}
      color={isActive ? "#367286" : "#757575"}
      letterSpacing="0.5px"
      sx={{
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      {label}
    </Typography>
  );
};

const TABS_CONFIG: TabConfig[] = [
  {
    path: "/settings/collections",
    label: "Saved Collections",
    featureFlagKey: "collectionsEnabled",
  },
  {
    path: "/settings/exports",
    label: "Export History",
  },
];

export const SettingsLayout: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCollectionsEnabled = useFeatureFlag("collectionsEnabled", false);

  const visibleTabs = useMemo(() => {
    return TABS_CONFIG.filter((tab) => {
      if (tab.featureFlagKey === "collectionsEnabled") {
        return isCollectionsEnabled;
      }
      return true;
    });
  }, [isCollectionsEnabled]);

  const activeTab = useMemo(() => {
    const tabIndex = visibleTabs.findIndex(
      (tab) => tab.path === location.pathname
    );
    return tabIndex >= 0 ? tabIndex : 0;
  }, [location.pathname, visibleTabs]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = visibleTabs[newValue];
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  const tabSlotProps = useMemo(
    () => ({ indicator: { style: { top: 0 } } }),
    []
  );

  // Redirect to first visible tab if on base /settings route
  useEffect(() => {
    if (location.pathname === "/settings" && visibleTabs.length > 0) {
      navigate(visibleTabs[0].path, { replace: true });
    }
  }, [location.pathname, visibleTabs, navigate]);

  // Breadcrumbs logic - automatically finds current tab
  const currentTab = TABS_CONFIG.find((tab) => tab.path === location.pathname);

  const renderBreadcrumbs = () => {
    if (!currentTab) return null;

    const breadcrumbLabel = currentTab.breadcrumbLabel || currentTab.label;

    return (
      <Breadcrumbs>
        <BreadcrumbItem label="Settings" />
        <BreadcrumbItem label={breadcrumbLabel} isActive />
      </Breadcrumbs>
    );
  };

  return (
    <AppLayout>
      <Stack direction="column" spacing={1} height="100%" flex={1} p={3}>
        {renderBreadcrumbs()}
        <Box pb={2}>
          <Typography
            fontSize="32px"
            fontWeight={300}
            color="#3a3a3a"
            lineHeight={1}
          >
            Settings
          </Typography>
        </Box>
        <Box borderTop={1} borderColor="divider">
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="settings tabs"
            slotProps={tabSlotProps}
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={tab.path}
                label={tab.label}
                id={`settings-tab-${index}`}
                aria-controls={`settings-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        <Box flexGrow={1} overflow="auto" sx={{ ...lightScrollbarStyle }}>
          <Outlet />
        </Box>
      </Stack>
    </AppLayout>
  );
};
